import TextEditorCustomize from '@/components/TextEditorCustomize'
import { DatacentralHookForm } from '@/components/ui/form-field'
import { QUERY_KEYS, QUERY_KEY_COMBINATIONS } from '@/constants/reactQueryKeys'
import { useModal } from '@/contexts/modal'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import useQueryClientCallback from '@/hooks/useQueryClientCallback'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import CheckIcons from '@/icons/CheckIcons'
import CloseIcons from '@/icons/CloseIcons'
import {
    getCustomerMailers,
    getGroupMailerTypes,
} from '@/modules/Customer/services'
import { CustomerMailer } from '@/modules/Customer/types'
import { updateServiceEntityName } from '@/modules/Service/services'
import {
    rejectInternalTicketWorkflowByStepId,
    resolveInternalTicketWorkflowByStepId,
} from '@/modules/Service/services/ticket'
import {
    InternalTicketWorkflowStepName,
    Service,
} from '@/modules/Service/types'
import { IPermissionProps } from '@/types/shared'
import React, { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Button } from 'vnetwork-ui'

interface CSKHVerificationFormProps extends IPermissionProps {
    stepId: number
    stepName: InternalTicketWorkflowStepName
    service?: Service
}

interface MailerFormValues {
    entity_name: string
    customer_mailer_id: number | null
}

const CSKHVerificationForm: React.FC<CSKHVerificationFormProps> = ({
    stepId,
    stepName,
    service,
}) => {
    const { t } = useTranslation()
    const { setModal } = useModal()
    const [showRejectForm, setShowRejectForm] = useState(false)
    const [rejectReason, setRejectReason] = useState('')

    const customerId = service?.customer_id

    const formControl = useForm<MailerFormValues>({
        defaultValues: {
            entity_name: service?.name || '',
            customer_mailer_id: null,
        },
    })

    const selectedMailerId = formControl.watch('customer_mailer_id')

    // Fetch active customer mailers
    const requiresMailerInput =
        stepName === InternalTicketWorkflowStepName.CSKHConfirm

    const { data: customerMailers } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.CUSTOMERS, 'mailers', customerId],
        queryFn: () => getCustomerMailers({ customerID: customerId! }),
        enabled: requiresMailerInput && !!customerId,
    })

    // Fetch mailer types to auto-fill when creating new mailer
    const { data: groupMailerTypes } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.GROUP_MAILER_TYPES],
        queryFn: () => getGroupMailerTypes(),
        enabled: requiresMailerInput,
    })

    // Filter service-related mailer type IDs (cấu hình, tài khoản, etc.)
    const serviceMailerTypeIds =
        groupMailerTypes?.results
            ?.filter(
                (type) =>
                    type.mailer_scope?.startsWith('service') &&
                    !type.mailer_hidden
            )
            ?.map((type) => type.mailer_type_id) || []

    // Auto-select newly created mailer (latest one)
    const mailerList = customerMailers?.results || []
    const [prevMailerCount, setPrevMailerCount] = useState(mailerList.length)

    useEffect(() => {
        // When mailer list grows (new mailer created via modal), auto-select it
        if (mailerList.length > prevMailerCount && mailerList.length > 0) {
            const newest = mailerList[mailerList.length - 1]
            formControl.setValue(
                'customer_mailer_id',
                newest.customer_mailer_id
            )
        }
        setPrevMailerCount(mailerList.length)
    }, [mailerList, prevMailerCount, formControl])

    const { refetch } = useQueryClientCallback({
        pattern: 'tickets',
        mode: 'contains',
    })
    const { refetch: refetchServices } = useQueryClientCallback({
        pattern: 'services',
        mode: 'contains',
    })

    const requiresEntityName =
        stepName === InternalTicketWorkflowStepName.TechKhoiTaoTrenDC

    const { mutateAsync: updateEntityName, isPending: isUpdatingEntity } =
        useMutationWithHandler(
            { mutationFn: updateServiceEntityName },
            {
                successMessage: t('update-service-entity-name-success'),
                reactQueryKeys: QUERY_KEYS.SERVICES,
                onSuccess: () => refetchServices(),
            }
        )

    const { mutateAsync: resolveStep, isPending: isResolving } =
        useMutationWithHandler(
            { mutationFn: resolveInternalTicketWorkflowByStepId },
            {
                successMessage: t('step-approved-success'),
                reactQueryKeys: QUERY_KEY_COMBINATIONS.SERVICE_WITH_TICKET,
                onSuccess: () => {
                    refetch()
                    refetchServices()
                },
            }
        )

    const { mutateAsync: rejectStep, isPending: isRejecting } =
        useMutationWithHandler(
            {
                mutationFn: async ({
                    stepId,
                    reason,
                }: {
                    stepId: number
                    reason: string
                }) => rejectInternalTicketWorkflowByStepId({ stepId, reason }),
            },
            {
                successMessage: t('step-rejected-success'),
                reactQueryKeys: QUERY_KEYS.TICKETS,
                onSuccess: () => {
                    refetch()
                    setShowRejectForm(false)
                    setRejectReason('')
                },
            }
        )

    // Open CreateCustomerMailerModal with pre-filled service & mailer types
    const handleCreateMailer = () => {
        if (!customerId) return
        setModal(
            'CustomerModals/MailerModals/CreateCustomerMailerModal/index',
            {
                customerID: customerId,
                defaultMailerTypeIds: serviceMailerTypeIds,
                defaultServiceIds: service?.service_id
                    ? [service.service_id]
                    : [],
            }
        )
    }

    const handleApprove = async () => {
        // Check employee_sale_tracking_ids for CSKHConfirm step
        if (stepName === InternalTicketWorkflowStepName.CSKHConfirm) {
            if (
                !service?.employee_sale_tracking_ids ||
                service?.employee_sale_tracking_ids.length === 0
            ) {
                toast.error(t('employee-sale-tracking-required'))
                return
            }
        }

        const values = formControl.getValues()

        // Update entity name for technical steps if changed
        if (
            requiresEntityName &&
            values.entity_name &&
            values.entity_name !== service?.name
        ) {
            await updateEntityName({
                serviceID: service?.service_id as number,
                name: values.entity_name,
            })
        }

        // Build filled_data with selected customer_mailer_id
        let filledData: Record<string, any> | undefined
        if (requiresMailerInput && values.customer_mailer_id) {
            const selectedMailer = mailerList.find(
                (m) =>
                    m.customer_mailer_id === Number(values.customer_mailer_id)
            )
            filledData = {
                customer_mailer_id: values.customer_mailer_id,
                mailer_receiver: selectedMailer?.mailer_receiver || '',
                mailer_cc: selectedMailer?.mailer_cc || '',
                mailer_bcc: selectedMailer?.mailer_bcc || '',
            }
        }

        await resolveStep({ stepId, filledData })
    }

    const handleReject = async () => {
        if (!rejectReason.trim()) {
            toast.error(t('reject-reason-required'))
            return
        }
        await rejectStep({ stepId, reason: rejectReason })
    }

    const isPending = isResolving || isRejecting || isUpdatingEntity

    const CREATE_NEW_VALUE = '__create_new__'

    const mailerOptions = [
        ...mailerList.map((m: CustomerMailer) => ({
            value: m.customer_mailer_id,
            label: `${m.customer_mailer_name} (${m.mailer_receiver})`,
        })),
        {
            value: CREATE_NEW_VALUE,
            label: `+ ${t('services:internal_tickets.create_new_mailer')}`,
        },
    ]

    // Show selected mailer details preview
    const selectedMailer = selectedMailerId
        ? mailerList.find(
              (m) => m.customer_mailer_id === Number(selectedMailerId)
          )
        : null

    const getStepConfig = () => {
        switch (stepName) {
            case InternalTicketWorkflowStepName.CSKHConfirm:
                return {
                    title: t('cskh-confirm-title'),
                    description: t('cskh-confirm-description'),
                    successMessage: t('cskh.confirm-request'),
                }
            case InternalTicketWorkflowStepName.CSKHVerifyCreateService:
                return {
                    title: t('cskh-verification-title'),
                    description: t('cskh-verification-description'),
                    successMessage: t('cskh.verification-success'),
                }
            case InternalTicketWorkflowStepName.CSKHConfirmCancel:
                return {
                    title: t('cskh-confirm-cancel-title'),
                    description: t('cskh-confirm-cancel-description'),
                    successMessage: t('cskh.confirm-request'),
                }
            case InternalTicketWorkflowStepName.TechnicalCancelService:
                return {
                    title: t('technical-cancel-service-title'),
                    description: t('technical-cancel-service-description'),
                    successMessage: t('technical.cancel-request'),
                }
            case InternalTicketWorkflowStepName.FinalDone:
                return {
                    title: t('final-completion-title'),
                    description: t('final-completion-description'),
                    successMessage: t('services:final.completion-success'),
                }
            case InternalTicketWorkflowStepName.CSKHXacNhan:
                return {
                    title: t('cskh-xac-nhan-title'),
                    description: t('cskh-xac-nhan-description'),
                    successMessage: t('cskh.confirm-request'),
                }
            case InternalTicketWorkflowStepName.TechKhoiTaoTrenDC:
                return {
                    title: t('tech-khoi-tao-tren-dc-title'),
                    description: t('tech-khoi-tao-tren-dc-description'),
                    successMessage: t('technical.create-service-success'),
                }
            case InternalTicketWorkflowStepName.CSKHKiemTraHoanTat:
                return {
                    title: t('cskh-kiem-tra-hoan-tat-title'),
                    description: t('cskh-kiem-tra-hoan-tat-description'),
                    successMessage: t('cskh.verification-success'),
                }
            default:
                return {
                    title: t('step-completion-title'),
                    description: t('step-completion-description'),
                    successMessage: t('step-completion-success'),
                }
        }
    }

    const stepConfig = getStepConfig()

    return (
        <div className="space-y-4">
            <div>
                <p className="text-sm font-medium mb-0.5">{stepConfig.title}</p>
                <p className="text-xs text-muted-foreground">
                    {stepConfig.description}
                </p>
            </div>

            {/* Form fields using DatacentralHookForm */}
            {(requiresEntityName || requiresMailerInput) && (
                <DatacentralHookForm<MailerFormValues>
                    formControl={formControl}
                    onSubmit={handleApprove}
                    subForm
                    columns={2}
                    data={[
                        // Entity name for technical steps
                        {
                            formItemProps: {
                                name: 'entity_name',
                                label: t('entity-name'),
                                placeholder: t('entity-name'),
                                type: 'text',
                                disabled: !!service?.name,
                            },
                            colSpan: 2,
                            hidden: !requiresEntityName,
                        },
                        // Select customer mailer (with "create new" option inside)
                        {
                            formItemProps: {
                                name: 'customer_mailer_id',
                                label: t(
                                    'services:internal_tickets.select_customer_mailer'
                                ),
                                type: 'select',
                                options: mailerOptions,
                                clearable: true,
                                onValueChange: (value: any) => {
                                    if (value === CREATE_NEW_VALUE) {
                                        // Reset to previous value & open modal
                                        formControl.setValue(
                                            'customer_mailer_id',
                                            null
                                        )
                                        handleCreateMailer()
                                    }
                                },
                            },
                            colSpan: 2,
                            hidden: !requiresMailerInput,
                        },
                    ]}
                />
            )}

            {/* Selected mailer preview */}
            {requiresMailerInput && selectedMailer && (
                <div className="bg-gray-50 rounded-md p-2 text-xs text-gray-600 space-y-1">
                    <div>
                        <span className="font-medium">
                            {t('services:internal_tickets.mailer_receiver')}:
                        </span>{' '}
                        {selectedMailer.mailer_receiver || '-'}
                    </div>
                    <div>
                        <span className="font-medium">
                            {t('services:internal_tickets.mailer_cc')}:
                        </span>{' '}
                        {selectedMailer.mailer_cc || '-'}
                    </div>
                    <div>
                        <span className="font-medium">
                            {t('services:internal_tickets.mailer_bcc')}:
                        </span>{' '}
                        {selectedMailer.mailer_bcc || '-'}
                    </div>
                </div>
            )}

            {!showRejectForm ? (
                <div className="flex items-center justify-end gap-2">
                    <Button
                        variant="outlined"
                        size="small"
                        color="success"
                        className="border-green-500"
                        startIcon={<CheckIcons className="w-4 h-4" />}
                        onClick={handleApprove}
                        loading={isResolving || isUpdatingEntity}
                        disabled={isPending}
                    >
                        {stepConfig.successMessage}
                    </Button>
                    <Button
                        variant="outlined"
                        color="error"
                        className="border-red-500"
                        size="small"
                        startIcon={<CloseIcons className="w-4 h-4" />}
                        onClick={() => setShowRejectForm(true)}
                        disabled={isPending}
                    >
                        {t('reject')}
                    </Button>
                </div>
            ) : (
                <div className="space-y-2">
                    <TextEditorCustomize
                        label={t('reject-reason')}
                        value={{
                            text: rejectReason,
                            attachments: [],
                        }}
                        className="bg-white rounded-md"
                        onChange={(e: any) => setRejectReason(e.text)}
                        placeholder={t('enter-reject-reason')}
                    />
                    <div className="flex items-center justify-end gap-2">
                        <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            onClick={handleReject}
                            loading={isRejecting}
                            disabled={isPending || !rejectReason.trim()}
                        >
                            {t('confirm-reject')}
                        </Button>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => {
                                setShowRejectForm(false)
                                setRejectReason('')
                            }}
                            disabled={isPending}
                        >
                            {t('cancel')}
                        </Button>
                    </div>
                </div>
            )}
        </div>
    )
}

export default CSKHVerificationForm
